import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Maximize2, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';

export default function StorePerformanceWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchStorePerformance();
  }, []);

  const fetchStorePerformance = async () => {
    try {
      const scorecard = await apiRequest('/store-scorecards/my-store');
      setData(scorecard);
    } catch (error) {
      console.error('Failed to load store performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getGrade = (score) => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 h-full" data-testid="store-performance-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-indigo-600" />
          <h3 className="font-semibold text-slate-900">Store Performance</h3>
        </div>
        {expandable && expandPath && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(expandPath)}
            className="h-8 w-8 p-0"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {data ? (
        <div className="space-y-4">
          {/* Overall Score */}
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className={`text-3xl font-bold ${getScoreColor(data.overall_score)}`}>
              {data.overall_score?.toFixed(0) || 0}%
            </div>
            <div className="text-sm text-slate-500">
              Grade: <span className="font-semibold">{getGrade(data.overall_score)}</span>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="p-2 bg-green-50 rounded">
              <div className="text-green-700 font-medium">{data.tasks_completed || 0}</div>
              <div className="text-green-600 text-xs">Tasks Done</div>
            </div>
            <div className="p-2 bg-blue-50 rounded">
              <div className="text-blue-700 font-medium">{data.compliance_rate || 0}%</div>
              <div className="text-blue-600 text-xs">Compliance</div>
            </div>
            <div className="p-2 bg-amber-50 rounded">
              <div className="text-amber-700 font-medium">{data.forms_submitted || 0}</div>
              <div className="text-amber-600 text-xs">Forms</div>
            </div>
            <div className="p-2 bg-purple-50 rounded">
              <div className="text-purple-700 font-medium">{data.temp_alerts || 0}</div>
              <div className="text-purple-600 text-xs">Temp Alerts</div>
            </div>
          </div>

          {/* Trend */}
          {data.trend !== undefined && (
            <div className={`flex items-center justify-center gap-1 text-sm ${data.trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {data.trend >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              <span>{data.trend >= 0 ? '+' : ''}{data.trend}% from last month</span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-4 text-slate-500 text-sm">
          <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No performance data</p>
        </div>
      )}
    </div>
  );
}
